// File: frontend/src/components/admin/AdminReports.tsx
import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { FaFilter, FaTimes, FaSearch } from "react-icons/fa";
import {
  EntryReportDto,
  EntryReportReason,
  AdminReportFilters,
  getAdminReports,
  dismissReport,
  actionReport,
} from "../../services/entryReportService";
import {
  MessageReportDto,
  AdminMessageReportFilters,
  getAdminMessageReports,
  dismissMessageReport,
  actionMessageReport,
} from "../../services/messageReportService";
import { Pagination } from "../../utils/Pagination";
import { useAutoDismiss } from "../../hooks/useAutoDismiss";
import { AdminSuccessBanner } from "../common/AdminBanner";
import { generateProfileUrl } from "../../utils";
import { generateMatchUrlFromEntry } from "../../utils/slugGenerator";
import { ADMIN_PAGE_SIZE, REPORT_RESOLUTION_NOTE_MAX_LENGTH } from "../../constants";
// Shared "current / max" counter shown under every free-text modal field
import { AdminCharCounter } from "./common";
import { formatAdminDate } from "../../utils/adminUtils";
import "./AdminReports.css";

interface AdminReportsProps {
  isAdmin: boolean;
}

// ── Shared ────────────────────────────────────────────────────────────────────

const STATUS_FILTERS: { value: string; labelKey: string }[] = [
  { value: "ALL",       labelKey: "admin_reports_filter_all" },
  { value: "PENDING",   labelKey: "admin_reports_filter_pending" },
  { value: "DISMISSED", labelKey: "admin_reports_filter_dismissed" },
  { value: "ACTIONED",  labelKey: "admin_reports_filter_actioned" },
];

// Reason options for the Entry Reports filter dropdown — includes the
// entry-content-quality reasons (AI_GENERATED, SPELLING_ERROR) that only
// ever appear on entry reports, never message reports.
const ENTRY_REASON_OPTIONS: EntryReportReason[] = [
  "SPAM",
  "PROFANITY",
  "HATE_SPEECH",
  "HARASSMENT",
  "MISINFORMATION",
  "PERSONAL_INFO",
  "INAPPROPRIATE",
  "AI_GENERATED",
  "SPELLING_ERROR",
  "NOT_A_DEFINITION",
  "OTHER",
];

// Reason options for the Message Reports filter dropdown — same subset
// offered in MessageReportModal.tsx, so this never shows a filter for a
// reason no message report can actually have.
const MESSAGE_REASON_OPTIONS: EntryReportReason[] = [
  "SPAM",
  "PROFANITY",
  "HATE_SPEECH",
  "HARASSMENT",
  "MISINFORMATION",
  "PERSONAL_INFO",
  "INAPPROPRIATE",
  "OTHER",
];

// ── Entry Reports section ─────────────────────────────────────────────────────

const EMPTY_ENTRY_FILTERS: AdminReportFilters = {
  status:           "PENDING",
  authorUsername:   "",
  reporterUsername: "",
  reason:           "",
  startDate:        "",
  endDate:          "",
};

interface EntryReportsSectionProps {
  isAdmin: boolean;
}

const EntryReportsSection: React.FC<EntryReportsSectionProps> = () => {
  const { t } = useTranslation();

  const [reports, setReports]         = useState<EntryReportDto[]>([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages]   = useState(0);
  const [totalItems, setTotalItems]   = useState(0);

  const [filters, setFilters] = useState<AdminReportFilters>(EMPTY_ENTRY_FILTERS);
  const [authorInput,   setAuthorInput]   = useState("");
  const [reporterInput, setReporterInput] = useState("");
  const [showFilters, setShowFilters] = useState(true);

  const [resolveModal, setResolveModal] = useState<{
    open: boolean;
    report: EntryReportDto | null;
    mode: "dismiss" | "action";
    note: string;
    submitting: boolean;
    error: string;
  }>({
    open: false,
    report: null,
    mode: "dismiss",
    note: "",
    submitting: false,
    error: "",
  });

  const [successMsg, setSuccessMsg, clearSuccessMsg] = useAutoDismiss();
  const [otherReasonPopup, setOtherReasonPopup] = useState<{ id: number; text: string } | null>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchReports = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const page = await getAdminReports(filters, currentPage, ADMIN_PAGE_SIZE);
      setReports(page.content);
      setTotalPages(page.totalPages);
      setTotalItems(page.totalItems);
    } catch {
      setError(t("admin_reports_fetch_error"));
    } finally {
      setLoading(false);
    }
  }, [filters, currentPage]); // intentionally omitting `t` — it changes reference on every render

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const applyTextFilters = () => {
    setFilters((prev) => ({
      ...prev,
      authorUsername:   authorInput.trim(),
      reporterUsername: reporterInput.trim(),
    }));
    setCurrentPage(0);
  };

  const handleStatusChange = (val: string) => {
    setFilters((prev) => ({ ...prev, status: val }));
    setCurrentPage(0);
  };

  const handleReasonChange = (val: string) => {
    setFilters((prev) => ({ ...prev, reason: val }));
    setCurrentPage(0);
  };
  const handleStartDate = (val: string) => {
    setFilters((prev) => ({ ...prev, startDate: val }));
    setCurrentPage(0);
  };
  const handleEndDate = (val: string) => {
    setFilters((prev) => ({ ...prev, endDate: val }));
    setCurrentPage(0);
  };

  const clearExtendedFilters = () => {
    setAuthorInput("");
    setReporterInput("");
    setFilters((prev) => ({
      ...prev,
      authorUsername:   "",
      reporterUsername: "",
      reason:           "",
      startDate:        "",
      endDate:          "",
    }));
    setCurrentPage(0);
  };

  const hasActiveExtendedFilters =
    !!filters.authorUsername ||
    !!filters.reporterUsername ||
    !!filters.reason ||
    !!filters.startDate ||
    !!filters.endDate;

  const openResolveModal = (report: EntryReportDto, mode: "dismiss" | "action") => {
    setResolveModal({ open: true, report, mode, note: "", submitting: false, error: "" });
  };

  const closeResolveModal = () => {
    setResolveModal((prev) => ({ ...prev, open: false }));
  };

  const handleResolve = async () => {
    if (!resolveModal.report) return;
    setResolveModal((prev) => ({ ...prev, submitting: true, error: "" }));
    try {
      if (resolveModal.mode === "dismiss") {
        await dismissReport(resolveModal.report.id, resolveModal.note || undefined);
        setSuccessMsg(t("admin_reports_dismiss_success"));
      } else {
        await actionReport(resolveModal.report.id, resolveModal.note || undefined);
        setSuccessMsg(t("admin_reports_action_success"));
      }
      closeResolveModal();
      fetchReports();
    } catch {
      setResolveModal((prev) => ({
        ...prev,
        submitting: false,
        error: t("admin_action_failed"),
      }));
    }
  };

  const reasonLabel = (r: string) => t(`report_reason_${r.toLowerCase()}`, r);

  return (
    <>
      {successMsg && <AdminSuccessBanner message={successMsg} onDismiss={clearSuccessMsg} />}

      {/* Toolbar — status tabs + filter toggle + count */}
      <div className="users-toolbar">
        <div className="admin-reports__filters">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              className={`admin-reports__filter-btn ${filters.status === f.value ? "active" : ""}`}
              onClick={() => handleStatusChange(f.value)}
              type="button"
            >
              {t(f.labelKey)}
            </button>
          ))}
        </div>

        <div className="admin-reports__toolbar-right">
          <button
            className={`filter-toggle ${showFilters || hasActiveExtendedFilters ? "active" : ""}`}
            onClick={() => setShowFilters((v) => !v)}
            type="button"
          >
            <FaFilter /> {t("admin_filters")}
          </button>

          {hasActiveExtendedFilters && (
            <button className="clear-filters" onClick={clearExtendedFilters} type="button">
              <FaTimes /> {t("admin_clear_filters")}
            </button>
          )}

          <span className="admin-reports__count">
            {t("admin_total_items", { count: totalItems })}
          </span>
        </div>
      </div>

      {/* Extended filter panel */}
      {showFilters && (
        <div className="admin-filter-panel">
          <div className="admin-filter-group">
            <label>{t("admin_reports_filter_author")}</label>
            <div className="search-box">
              <input
                type="text"
                placeholder={t("admin_reports_filter_author_placeholder")}
                value={authorInput}
                onChange={(e) => setAuthorInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && applyTextFilters()}
              />
              <button className="search-btn" onClick={applyTextFilters} type="button">
                <FaSearch />
              </button>
            </div>
          </div>

          <div className="admin-filter-group">
            <label>{t("admin_reports_filter_reporter")}</label>
            <div className="search-box">
              <input
                type="text"
                placeholder={t("admin_reports_filter_reporter_placeholder")}
                value={reporterInput}
                onChange={(e) => setReporterInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && applyTextFilters()}
              />
              <button className="search-btn" onClick={applyTextFilters} type="button">
                <FaSearch />
              </button>
            </div>
          </div>

          {/* Dropdown — triggers fetch immediately */}
          <div className="admin-filter-group">
            <label>{t("admin_reports_col_reason")}</label>
            <select value={filters.reason} onChange={(e) => handleReasonChange(e.target.value)}>
              <option value="">{t("admin_reports_filter_reason_all")}</option>
              {ENTRY_REASON_OPTIONS.map((r) => (
                <option key={r} value={r}>{reasonLabel(r)}</option>
              ))}
            </select>
          </div>

          {/* Date pickers — trigger fetch immediately */}
          <div className="admin-filter-group">
            <label>{t("admin_filter_date_from")}</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleStartDate(e.target.value)}
            />
          </div>

          <div className="admin-filter-group">
            <label>{t("admin_filter_date_to")}</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleEndDate(e.target.value)}
            />
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="admin-reports__loading">{t("loading")}</div>
      ) : error ? (
        <div className="admin-reports__error">{error}</div>
      ) : reports.length === 0 ? (
        <div className="admin-reports__empty">{t("admin_reports_empty")}</div>
      ) : (
        <div className="admin-reports__table-wrapper">
          <table className="admin-reports__table">
            <thead>
              <tr>
                <th>{t("admin_reports_col_id")}</th>
                <th>{t("admin_reports_col_entry")}</th>
                <th>{t("admin_reports_col_author")}</th>
                <th>{t("admin_reports_col_reporter")}</th>
                <th>{t("admin_reports_col_reason")}</th>
                <th>{t("admin_reports_col_date")}</th>
                <th>{t("admin_reports_col_status")}</th>
                <th>{t("admin_reports_col_actions")}</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((r) => (
                <tr key={r.id} className={`admin-reports__row admin-reports__row--${r.status.toLowerCase()}`}>
                  <td className="admin-reports__cell-id">#{r.id}</td>

                  {/* Entry cell — link navigates to the entry's page (match/club/person/team) */}
                  <td className="admin-reports__cell-entry">
                    {r.matchId && r.homeTeam && r.awayTeam && r.matchDate ? (
                      <Link
                        to={`${generateMatchUrlFromEntry(r.homeTeam, r.awayTeam, new Date(r.matchDate + "T00:00:00"), r.matchId)}#entry-${r.entryId}`}
                        className="admin-reports__entry-link"
                        title={`Entry #${r.entryId}`}
                      >
                        #{r.entryId}
                      </Link>
                    ) : r.clubSlug ? (
                      <Link
                        to={`/kulup/${r.clubSlug}#entry-${r.entryId}`}
                        className="admin-reports__entry-link"
                        title={`Entry #${r.entryId}`}
                      >
                        #{r.entryId}
                      </Link>
                    ) : r.personSlug ? (
                      <Link
                        to={`/kisi/${r.personSlug}#entry-${r.entryId}`}
                        className="admin-reports__entry-link"
                        title={`Entry #${r.entryId}`}
                      >
                        #{r.entryId}
                      </Link>
                    ) : r.teamClubSlug && r.teamSportSlug ? (
                      <Link
                        to={`/takim/${r.teamClubSlug}/${r.teamSportSlug}#entry-${r.entryId}`}
                        className="admin-reports__entry-link"
                        title={`Entry #${r.entryId}`}
                      >
                        #{r.entryId}
                      </Link>
                    ) : r.topicSlug ? (
                      <Link
                        to={`/gundem/${r.topicSlug}#entry-${r.entryId}`}
                        className="admin-reports__entry-link"
                        title={`Entry #${r.entryId}`}
                      >
                        #{r.entryId}
                      </Link>
                    ) : (
                      <span className="admin-reports__entry-link">#{r.entryId}</span>
                    )}
                    <span className="admin-reports__entry-preview">
                      {r.entryContent}
                    </span>
                  </td>

                  <td>
                    <Link
                      to={generateProfileUrl(r.entryAuthorUsername, r.entryAuthorId)}
                      className="admin-reports__user-link"
                      title={`@${r.entryAuthorUsername}`}
                    >
                      @{r.entryAuthorUsername}
                    </Link>
                  </td>

                  <td>
                    <Link
                      to={generateProfileUrl(r.reporterUsername, r.reporterUserId)}
                      className="admin-reports__user-link"
                      title={`@${r.reporterUsername}`}
                    >
                      @{r.reporterUsername}
                    </Link>
                  </td>

                  <td>
                    <div className="admin-reports__reason-wrapper">
                      <span
                        className={`admin-reports__reason-badge ${r.otherReason ? "admin-reports__reason-badge--clickable" : ""}`}
                        onClick={() => r.otherReason && setOtherReasonPopup({ id: r.id, text: r.otherReason })}
                      >
                        {reasonLabel(r.reason)}
                        {r.otherReason && <span className="admin-reports__reason-expand"> ▸</span>}
                      </span>
                    </div>
                  </td>

                  <td className="admin-reports__cell-date">
                    {formatAdminDate(r.createdAt)}
                  </td>

                  <td>
                    <span className={`admin-reports__status-badge admin-reports__status-badge--${r.status.toLowerCase()}`}>
                      {t(`admin_reports_status_${r.status.toLowerCase()}`)}
                    </span>
                  </td>

                  <td className="admin-reports__cell-actions">
                    {r.status === "PENDING" && (
                      <>
                        <button
                          className="admin-reports__btn admin-reports__btn--dismiss"
                          onClick={() => openResolveModal(r, "dismiss")}
                          type="button"
                        >
                          {t("admin_reports_dismiss")}
                        </button>
                        <button
                          className="admin-reports__btn admin-reports__btn--action"
                          onClick={() => openResolveModal(r, "action")}
                          type="button"
                        >
                          {t("admin_reports_action")}
                        </button>
                      </>
                    )}
                    {r.status !== "PENDING" && (
                      <div className="admin-reports__resolved-info">
                        {r.resolvedByUsername && (
                          <span className="admin-reports__resolved-by">
                            @{r.resolvedByUsername}
                            {r.resolvedAt && ` · ${formatAdminDate(r.resolvedAt)}`}
                          </span>
                        )}
                        {r.resolutionNote && (
                          <span className="admin-reports__resolution-note" title={r.resolutionNote}>
                            {r.resolutionNote.length > 60
                              ? r.resolutionNote.slice(0, 60) + "…"
                              : r.resolutionNote}
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          paginate={setCurrentPage}
        />
      )}

      {/* Other reason popup */}
      {otherReasonPopup && (
        <div className="admin-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) { setOtherReasonPopup(null) } }}>
          <div className="admin-modal other-reason-modal">
            <div className="admin-modal-header">
              <h3>{t("admin_reports_other_reason_title")}</h3>
              <button className="modal-close-btn" onClick={() => setOtherReasonPopup(null)}>×</button>
            </div>
            <div className="admin-modal-body">
              <p className="other-reason-modal__text">{otherReasonPopup.text}</p>
            </div>
            <div className="admin-modal-footer">
              <button className="modal-cancel-btn" onClick={() => setOtherReasonPopup(null)} type="button">
                {t("close")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resolve modal (dismiss / action) */}
      {resolveModal.open && resolveModal.report && (
        <div
          className="admin-modal-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) closeResolveModal(); }}
        >
          <div className="admin-modal report-resolve-modal">
            <div className="admin-modal-header">
              <h3>
                {resolveModal.mode === "dismiss"
                  ? t("admin_reports_dismiss_title", { id: resolveModal.report.id })
                  : t("admin_reports_action_title", { id: resolveModal.report.id })}
              </h3>
              <button className="modal-close-btn" onClick={closeResolveModal}>×</button>
            </div>

            <div className="admin-modal-body">
              <div className="report-resolve-modal__preview">
                <span className="report-resolve-modal__preview-label">
                  {t("admin_reports_entry_preview_label")}
                </span>
                <p className="report-resolve-modal__preview-text">
                  {resolveModal.report.entryContent}
                </p>
              </div>

              <div className="report-resolve-modal__meta">
                <span>{t("admin_reports_col_author")}: </span>
                <strong>@{resolveModal.report.entryAuthorUsername}</strong>
                {" · "}
                <span>{t("admin_reports_col_reason")}: </span>
                <strong>{reasonLabel(resolveModal.report.reason)}</strong>
              </div>

              <label className="report-resolve-modal__note-label">
                {t("admin_reports_note_label")}
              </label>
              <textarea
                className="report-resolve-modal__note-textarea"
                placeholder={t("admin_reports_note_placeholder")}
                value={resolveModal.note}
                onChange={(e) =>
                  setResolveModal((prev) => ({ ...prev, note: e.target.value }))
                }
                maxLength={REPORT_RESOLUTION_NOTE_MAX_LENGTH}
                rows={3}
                disabled={resolveModal.submitting}
              />
              <AdminCharCounter value={resolveModal.note} max={REPORT_RESOLUTION_NOTE_MAX_LENGTH} />

              {resolveModal.error && (
                <p className="report-resolve-modal__error">{resolveModal.error}</p>
              )}
            </div>

            <div className="admin-modal-footer">
              <button
                className="modal-cancel-btn"
                onClick={closeResolveModal}
                disabled={resolveModal.submitting}
                type="button"
              >
                {t("admin_bio_modal_cancel")}
              </button>
              <button
                className={`modal-confirm-btn ${resolveModal.mode === "action" ? "modal-confirm-btn--danger" : ""}`}
                onClick={handleResolve}
                disabled={resolveModal.submitting}
                type="button"
              >
                {resolveModal.submitting
                  ? "..."
                  : resolveModal.mode === "dismiss"
                  ? t("admin_reports_dismiss_confirm")
                  : t("admin_reports_action_confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ── Message Reports section ───────────────────────────────────────────────────

const EMPTY_MSG_FILTERS: AdminMessageReportFilters = {
  status:           "PENDING",
  senderUsername:   "",
  reporterUsername: "",
  reason:           "",
  startDate:        "",
  endDate:          "",
};

interface MessageReportsSectionProps {
  isAdmin: boolean;
}

const MessageReportsSection: React.FC<MessageReportsSectionProps> = () => {
  const { t } = useTranslation();

  const [reports, setReports]         = useState<MessageReportDto[]>([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages]   = useState(0);
  const [totalItems, setTotalItems]   = useState(0);

  const [filters, setFilters] = useState<AdminMessageReportFilters>(EMPTY_MSG_FILTERS);
  const [senderInput,   setSenderInput]   = useState("");
  const [reporterInput, setReporterInput] = useState("");
  const [showFilters, setShowFilters] = useState(true);

  const [resolveModal, setResolveModal] = useState<{
    open: boolean;
    report: MessageReportDto | null;
    mode: "dismiss" | "action";
    note: string;
    submitting: boolean;
    error: string;
  }>({
    open: false,
    report: null,
    mode: "dismiss",
    note: "",
    submitting: false,
    error: "",
  });

  const [successMsg, setSuccessMsg, clearSuccessMsg] = useAutoDismiss();
  const [otherReasonPopup, setOtherReasonPopup] = useState<{ id: number; text: string } | null>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchReports = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const page = await getAdminMessageReports(filters, currentPage, ADMIN_PAGE_SIZE);
      setReports(page.content);
      setTotalPages(page.totalPages);
      setTotalItems(page.totalItems);
    } catch {
      setError(t("admin_reports_fetch_error"));
    } finally {
      setLoading(false);
    }
  }, [filters, currentPage]); // intentionally omitting `t` — it changes reference on every render

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const applyTextFilters = () => {
    setFilters((prev) => ({
      ...prev,
      senderUsername:   senderInput.trim(),
      reporterUsername: reporterInput.trim(),
    }));
    setCurrentPage(0);
  };

  const handleStatusChange = (val: string) => {
    setFilters((prev) => ({ ...prev, status: val }));
    setCurrentPage(0);
  };

  const handleReasonChange = (val: string) => {
    setFilters((prev) => ({ ...prev, reason: val }));
    setCurrentPage(0);
  };
  const handleStartDate = (val: string) => {
    setFilters((prev) => ({ ...prev, startDate: val }));
    setCurrentPage(0);
  };
  const handleEndDate = (val: string) => {
    setFilters((prev) => ({ ...prev, endDate: val }));
    setCurrentPage(0);
  };

  const clearExtendedFilters = () => {
    setSenderInput("");
    setReporterInput("");
    setFilters((prev) => ({
      ...prev,
      senderUsername:   "",
      reporterUsername: "",
      reason:           "",
      startDate:        "",
      endDate:          "",
    }));
    setCurrentPage(0);
  };

  const hasActiveExtendedFilters =
    !!filters.senderUsername ||
    !!filters.reporterUsername ||
    !!filters.reason ||
    !!filters.startDate ||
    !!filters.endDate;

  const openResolveModal = (report: MessageReportDto, mode: "dismiss" | "action") => {
    setResolveModal({ open: true, report, mode, note: "", submitting: false, error: "" });
  };

  const closeResolveModal = () => {
    setResolveModal((prev) => ({ ...prev, open: false }));
  };

  const handleResolve = async () => {
    if (!resolveModal.report) return;
    setResolveModal((prev) => ({ ...prev, submitting: true, error: "" }));
    try {
      if (resolveModal.mode === "dismiss") {
        await dismissMessageReport(resolveModal.report.id, resolveModal.note || undefined);
        setSuccessMsg(t("admin_reports_dismiss_success"));
      } else {
        await actionMessageReport(resolveModal.report.id, resolveModal.note || undefined);
        setSuccessMsg(t("admin_reports_action_success"));
      }
      closeResolveModal();
      fetchReports();
    } catch {
      setResolveModal((prev) => ({
        ...prev,
        submitting: false,
        error: t("admin_action_failed"),
      }));
    }
  };

  const reasonLabel = (r: string) => t(`report_reason_${r.toLowerCase()}`, r);

  return (
    <>
      {successMsg && <AdminSuccessBanner message={successMsg} onDismiss={clearSuccessMsg} />}

      {/* Toolbar — status tabs + filter toggle + count */}
      <div className="users-toolbar">
        <div className="admin-reports__filters">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              className={`admin-reports__filter-btn ${filters.status === f.value ? "active" : ""}`}
              onClick={() => handleStatusChange(f.value)}
              type="button"
            >
              {t(f.labelKey)}
            </button>
          ))}
        </div>

        <div className="admin-reports__toolbar-right">
          <button
            className={`filter-toggle ${showFilters || hasActiveExtendedFilters ? "active" : ""}`}
            onClick={() => setShowFilters((v) => !v)}
            type="button"
          >
            <FaFilter /> {t("admin_filters")}
          </button>

          {hasActiveExtendedFilters && (
            <button className="clear-filters" onClick={clearExtendedFilters} type="button">
              <FaTimes /> {t("admin_clear_filters")}
            </button>
          )}

          <span className="admin-reports__count">
            {t("admin_total_items", { count: totalItems })}
          </span>
        </div>
      </div>

      {/* Extended filter panel */}
      {showFilters && (
        <div className="admin-filter-panel">
          {/* Sender username filter */}
          <div className="admin-filter-group">
            <label>{t("admin_msg_reports_filter_sender")}</label>
            <div className="search-box">
              <input
                type="text"
                placeholder={t("admin_msg_reports_filter_sender_placeholder")}
                value={senderInput}
                onChange={(e) => setSenderInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && applyTextFilters()}
              />
              <button className="search-btn" onClick={applyTextFilters} type="button">
                <FaSearch />
              </button>
            </div>
          </div>

          <div className="admin-filter-group">
            <label>{t("admin_reports_filter_reporter")}</label>
            <div className="search-box">
              <input
                type="text"
                placeholder={t("admin_reports_filter_reporter_placeholder")}
                value={reporterInput}
                onChange={(e) => setReporterInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && applyTextFilters()}
              />
              <button className="search-btn" onClick={applyTextFilters} type="button">
                <FaSearch />
              </button>
            </div>
          </div>

          <div className="admin-filter-group">
            <label>{t("admin_reports_col_reason")}</label>
            <select value={filters.reason} onChange={(e) => handleReasonChange(e.target.value)}>
              <option value="">{t("admin_reports_filter_reason_all")}</option>
              {MESSAGE_REASON_OPTIONS.map((r) => (
                <option key={r} value={r}>{reasonLabel(r)}</option>
              ))}
            </select>
          </div>

          <div className="admin-filter-group">
            <label>{t("admin_filter_date_from")}</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleStartDate(e.target.value)}
            />
          </div>

          <div className="admin-filter-group">
            <label>{t("admin_filter_date_to")}</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleEndDate(e.target.value)}
            />
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="admin-reports__loading">{t("loading")}</div>
      ) : error ? (
        <div className="admin-reports__error">{error}</div>
      ) : reports.length === 0 ? (
        <div className="admin-reports__empty">{t("admin_reports_empty")}</div>
      ) : (
        <div className="admin-reports__table-wrapper">
          <table className="admin-reports__table">
            <thead>
              <tr>
                <th>{t("admin_reports_col_id")}</th>
                <th>{t("admin_msg_reports_col_message")}</th>
                <th>{t("admin_msg_reports_col_sender")}</th>
                <th>{t("admin_reports_col_reporter")}</th>
                <th>{t("admin_reports_col_reason")}</th>
                <th>{t("admin_reports_col_date")}</th>
                <th>{t("admin_reports_col_status")}</th>
                <th>{t("admin_reports_col_actions")}</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((r) => (
                <tr key={r.id} className={`admin-reports__row admin-reports__row--${r.status.toLowerCase()}`}>
                  <td className="admin-reports__cell-id">#{r.id}</td>

                  {/* Message preview cell */}
                  <td className="admin-reports__cell-entry">
                    <span className="admin-reports__entry-preview">
                      {r.messageContent}
                    </span>
                  </td>

                  {/* Sender (the user who sent the reported message) */}
                  <td>
                    <Link
                      to={generateProfileUrl(r.messageSenderUsername, r.messageSenderId)}
                      className="admin-reports__user-link"
                      title={`@${r.messageSenderUsername}`}
                    >
                      @{r.messageSenderUsername}
                    </Link>
                  </td>

                  {/* Reporter (the user who submitted the complaint) */}
                  <td>
                    <Link
                      to={generateProfileUrl(r.reporterUsername, r.reporterUserId)}
                      className="admin-reports__user-link"
                      title={`@${r.reporterUsername}`}
                    >
                      @{r.reporterUsername}
                    </Link>
                  </td>

                  <td>
                    <div className="admin-reports__reason-wrapper">
                      <span
                        className={`admin-reports__reason-badge ${r.otherReason ? "admin-reports__reason-badge--clickable" : ""}`}
                        onClick={() => r.otherReason && setOtherReasonPopup({ id: r.id, text: r.otherReason! })}
                      >
                        {reasonLabel(r.reason)}
                        {r.otherReason && <span className="admin-reports__reason-expand"> ▸</span>}
                      </span>
                    </div>
                  </td>

                  <td className="admin-reports__cell-date">
                    {formatAdminDate(r.createdAt)}
                  </td>

                  <td>
                    <span className={`admin-reports__status-badge admin-reports__status-badge--${r.status.toLowerCase()}`}>
                      {t(`admin_reports_status_${r.status.toLowerCase()}`)}
                    </span>
                  </td>

                  <td className="admin-reports__cell-actions">
                    {r.status === "PENDING" && (
                      <>
                        <button
                          className="admin-reports__btn admin-reports__btn--dismiss"
                          onClick={() => openResolveModal(r, "dismiss")}
                          type="button"
                        >
                          {t("admin_reports_dismiss")}
                        </button>
                        <button
                          className="admin-reports__btn admin-reports__btn--action"
                          onClick={() => openResolveModal(r, "action")}
                          type="button"
                        >
                          {t("admin_reports_action")}
                        </button>
                      </>
                    )}
                    {r.status !== "PENDING" && (
                      <div className="admin-reports__resolved-info">
                        {r.resolvedByUsername && (
                          <span className="admin-reports__resolved-by">
                            @{r.resolvedByUsername}
                            {r.resolvedAt && ` · ${formatAdminDate(r.resolvedAt)}`}
                          </span>
                        )}
                        {r.resolutionNote && (
                          <span className="admin-reports__resolution-note" title={r.resolutionNote}>
                            {r.resolutionNote.length > 60
                              ? r.resolutionNote.slice(0, 60) + "…"
                              : r.resolutionNote}
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          paginate={setCurrentPage}
        />
      )}

      {/* Other reason popup */}
      {otherReasonPopup && (
        <div className="admin-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) { setOtherReasonPopup(null) } }}>
          <div className="admin-modal other-reason-modal">
            <div className="admin-modal-header">
              <h3>{t("admin_reports_other_reason_title")}</h3>
              <button className="modal-close-btn" onClick={() => setOtherReasonPopup(null)}>×</button>
            </div>
            <div className="admin-modal-body">
              <p className="other-reason-modal__text">{otherReasonPopup.text}</p>
            </div>
            <div className="admin-modal-footer">
              <button className="modal-cancel-btn" onClick={() => setOtherReasonPopup(null)} type="button">
                {t("close")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resolve modal (dismiss / action) */}
      {resolveModal.open && resolveModal.report && (
        <div
          className="admin-modal-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) closeResolveModal(); }}
        >
          <div className="admin-modal report-resolve-modal">
            <div className="admin-modal-header">
              <h3>
                {resolveModal.mode === "dismiss"
                  ? t("admin_reports_dismiss_title", { id: resolveModal.report.id })
                  : t("admin_reports_action_title", { id: resolveModal.report.id })}
              </h3>
              <button className="modal-close-btn" onClick={closeResolveModal}>×</button>
            </div>

            <div className="admin-modal-body">
              {/* Message preview */}
              <div className="report-resolve-modal__preview">
                <span className="report-resolve-modal__preview-label">
                  {t("admin_msg_reports_message_preview_label")}
                </span>
                <p className="report-resolve-modal__preview-text">
                  {resolveModal.report.messageContent}
                </p>
              </div>

              <div className="report-resolve-modal__meta">
                <span>{t("admin_msg_reports_col_sender")}: </span>
                <strong>@{resolveModal.report.messageSenderUsername}</strong>
                {" · "}
                <span>{t("admin_reports_col_reason")}: </span>
                <strong>{reasonLabel(resolveModal.report.reason)}</strong>
              </div>

              <label className="report-resolve-modal__note-label">
                {t("admin_reports_note_label")}
              </label>
              <textarea
                className="report-resolve-modal__note-textarea"
                placeholder={t("admin_reports_note_placeholder")}
                value={resolveModal.note}
                onChange={(e) =>
                  setResolveModal((prev) => ({ ...prev, note: e.target.value }))
                }
                maxLength={REPORT_RESOLUTION_NOTE_MAX_LENGTH}
                rows={3}
                disabled={resolveModal.submitting}
              />
              <AdminCharCounter value={resolveModal.note} max={REPORT_RESOLUTION_NOTE_MAX_LENGTH} />

              {resolveModal.error && (
                <p className="report-resolve-modal__error">{resolveModal.error}</p>
              )}
            </div>

            <div className="admin-modal-footer">
              <button
                className="modal-cancel-btn"
                onClick={closeResolveModal}
                disabled={resolveModal.submitting}
                type="button"
              >
                {t("admin_bio_modal_cancel")}
              </button>
              <button
                className={`modal-confirm-btn ${resolveModal.mode === "action" ? "modal-confirm-btn--danger" : ""}`}
                onClick={handleResolve}
                disabled={resolveModal.submitting}
                type="button"
              >
                {resolveModal.submitting
                  ? "..."
                  : resolveModal.mode === "dismiss"
                  ? t("admin_reports_dismiss_confirm")
                  : t("admin_reports_action_confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ── Main tabbed container ─────────────────────────────────────────────────────

type ReportTab = "entries" | "messages";

export const AdminReports: React.FC<AdminReportsProps> = ({ isAdmin }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<ReportTab>("entries");

  return (
    <div className="admin-reports">
      {/* Top-level tab switcher: Entry Reports vs Message Reports */}
      <div className="admin-reports__type-tabs">
        <button
          className={`admin-reports__type-tab ${activeTab === "entries" ? "active" : ""}`}
          onClick={() => setActiveTab("entries")}
          type="button"
        >
          {t("admin_reports_tab_entries")}
        </button>
        <button
          className={`admin-reports__type-tab ${activeTab === "messages" ? "active" : ""}`}
          onClick={() => setActiveTab("messages")}
          type="button"
        >
          {t("admin_reports_tab_messages")}
        </button>
      </div>

      {activeTab === "entries" ? (
        <EntryReportsSection isAdmin={isAdmin} />
      ) : (
        <MessageReportsSection isAdmin={isAdmin} />
      )}
    </div>
  );
};